import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, X, FileText, ExternalLink, Check, MapPin } from "lucide-react";

export default function Admin() {
    const { user, loading } = useAuth();
    const [pending, setPending] = useState([]);
    const [isAdmin, setIsAdmin] = useState(null);
    const [preview, setPreview] = useState(null); // { coach_id, cert_index, name, file_data_url }

    const reload = async () => {
        try {
            await api.get("/admin/me");
            setIsAdmin(true);
            const { data } = await api.get("/admin/coaches/pending");
            setPending(data);
        } catch {
            setIsAdmin(false);
        }
    };

    useEffect(() => {
        if (!loading && user) reload();
        else if (!loading && !user) setIsAdmin(false);
    }, [loading, user]);

    const approve = async (coach_id) => {
        try {
            await api.post(`/admin/coaches/${coach_id}/approve`);
            toast.success("Coach approuvé", {
                description: "Son profil est désormais vérifié et les alertes partent.",
            });
            reload();
        } catch {
            toast.error("Échec de l'approbation");
        }
    };

    const reject = async (coach_id) => {
        if (!window.confirm("Rejeter et supprimer ce profil ?")) return;
        try {
            await api.post(`/admin/coaches/${coach_id}/reject`);
            toast.success("Candidature rejetée");
            reload();
        } catch {
            toast.error("Échec du rejet");
        }
    };

    const openDiploma = async (coach_id, cert_index, cert_name) => {
        try {
            const { data } = await api.get(
                `/admin/coaches/${coach_id}/diploma/${cert_index}`
            );
            setPreview({ coach_id, cert_index, name: cert_name, file_data_url: data.file_data_url });
        } catch {
            toast.error("Aucun fichier joint");
        }
    };

    if (loading || isAdmin === null)
        return <div className="p-12 text-neutral-500">Chargement...</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (!isAdmin)
        return (
            <div className="mx-auto max-w-[600px] px-6 py-24 text-center">
                <h1 className="font-display text-4xl uppercase text-white">
                    Accès refusé
                </h1>
                <p className="mt-3 font-mono text-xs uppercase tracking-widest text-neutral-500">
                    Cette page est réservée aux administrateurs EarnIt.
                </p>
                <Link
                    to="/"
                    className="mt-6 inline-block border border-white/20 px-4 py-2 text-xs uppercase tracking-widest text-white hover:border-[#FF2E2E]"
                >
                    Retour à l&apos;annuaire
                </Link>
            </div>
        );

    return (
        <div className="mx-auto max-w-[1400px] px-6 py-10">
            <div className="mb-8 flex items-end justify-between">
                <div>
                    <div className="inline-flex items-center gap-2 border border-white/10 px-3 py-1.5">
                        <ShieldCheck className="h-3 w-3 text-[#FF2E2E]" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">
                            Vérification coachs
                        </span>
                    </div>
                    <h1 className="mt-3 font-display text-5xl uppercase leading-none tracking-tight text-white">
                        Validation Admin
                    </h1>
                </div>
                <div className="border border-white/10 bg-[#121212] p-4 text-right">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                        En attente
                    </div>
                    <div className="font-display text-4xl text-[#FF2E2E]">
                        {pending.length}
                    </div>
                </div>
            </div>

            {pending.length === 0 ? (
                <div className="border border-dashed border-white/10 p-12 text-center font-mono text-xs uppercase tracking-widest text-neutral-500">
                    Aucune candidature en attente. Tout est à jour.
                </div>
            ) : (
                <div
                    data-testid="admin-pending-list"
                    className="grid grid-cols-1 gap-4 md:grid-cols-2"
                >
                    {pending.map((c) => (
                        <article
                            key={c.coach_id}
                            data-testid={`admin-card-${c.coach_id}`}
                            className="flex flex-col border border-white/10 bg-[#121212]"
                        >
                            <div className="flex items-stretch gap-4 border-b border-white/10 p-4">
                                <div className="h-20 w-20 flex-shrink-0 overflow-hidden border border-white/10 bg-black">
                                    {c.photo_url ? (
                                        <img
                                            src={c.photo_url}
                                            alt={c.name}
                                            className="h-full w-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex h-full w-full items-center justify-center font-display text-3xl text-neutral-700">
                                            {(c.name || "?")[0]}
                                        </div>
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <h2 className="font-display text-2xl uppercase leading-none tracking-tight text-white">
                                                {c.name}
                                            </h2>
                                            <div className="mt-2 flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                                <span className="inline-flex items-center gap-1">
                                                    <MapPin className="h-3 w-3" /> {c.city}
                                                    {c.remote && " · À distance"}
                                                </span>
                                                <span>·</span>
                                                <span>{c.age} ans</span>
                                            </div>
                                        </div>
                                        <span
                                            className={`font-mono text-[9px] uppercase tracking-[0.3em] px-2 py-1 ${
                                                c.category === "physical"
                                                    ? "bg-[#FF2E2E] text-white"
                                                    : "border border-white/40 bg-black/60 text-white"
                                            }`}
                                        >
                                            {c.category === "physical" ? "Corps" : "Esprit"}
                                        </span>
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {(c.specialties || []).map((s) => (
                                            <span
                                                key={s}
                                                className="border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-widest text-neutral-300"
                                            >
                                                {s}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="flex-1 p-4">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                    Diplômes déclarés
                                </div>
                                <ul className="mt-2 space-y-2">
                                    {(c.certifications || []).map((cert, i) => (
                                        <li
                                            key={i}
                                            className="flex items-center justify-between border border-white/10 bg-[#0a0a0a] p-3"
                                        >
                                            <div className="min-w-0">
                                                <div className="truncate font-display text-base uppercase text-white">
                                                    {cert.name}
                                                </div>
                                                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                                    {cert.issuer || "Organisme non renseigné"}
                                                </div>
                                            </div>
                                            {cert.file_data_url ? (
                                                <button
                                                    data-testid={`admin-view-diploma-${c.coach_id}-${i}`}
                                                    onClick={() =>
                                                        openDiploma(c.coach_id, i, cert.name)
                                                    }
                                                    className="inline-flex items-center gap-1 border border-[#FF2E2E] bg-[#FF2E2E]/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-[#FF2E2E] hover:bg-[#FF2E2E] hover:text-white"
                                                >
                                                    <FileText className="h-3 w-3" /> Voir
                                                </button>
                                            ) : (
                                                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-600">
                                                    Pas de fichier
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                                {c.apply_email && (
                                    <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                        Contact : <span className="text-white">{c.apply_email}</span>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 border-t border-white/10">
                                <button
                                    data-testid={`admin-reject-${c.coach_id}`}
                                    onClick={() => reject(c.coach_id)}
                                    className="flex items-center justify-center gap-2 border-r border-white/10 py-3 font-mono text-[11px] uppercase tracking-widest text-neutral-400 transition-colors hover:bg-black hover:text-white"
                                >
                                    <X className="h-3.5 w-3.5" />
                                    Rejeter
                                </button>
                                <button
                                    data-testid={`admin-approve-${c.coach_id}`}
                                    onClick={() => approve(c.coach_id)}
                                    className="flex items-center justify-center gap-2 bg-[#FF2E2E] py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-[#D61F1F]"
                                >
                                    <Check className="h-3.5 w-3.5" />
                                    Approuver
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            )}

            <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
                <DialogContent className="rounded-none border border-white/10 bg-[#0A0A0A] text-white sm:max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl uppercase tracking-tight">
                            {preview?.name || "Diplôme"}
                        </DialogTitle>
                    </DialogHeader>
                    {preview?.file_data_url && (
                        <div className="space-y-3">
                            {preview.file_data_url.startsWith("data:application/pdf") ? (
                                <iframe
                                    title="diploma"
                                    src={preview.file_data_url}
                                    className="h-[70vh] w-full border border-white/10 bg-black"
                                />
                            ) : (
                                <img
                                    src={preview.file_data_url}
                                    alt={preview.name}
                                    className="max-h-[70vh] w-full border border-white/10 bg-black object-contain"
                                />
                            )}
                            <a
                                href={preview.file_data_url}
                                download
                                className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:border-[#FF2E2E]"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                Télécharger / Ouvrir
                            </a>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
